// ─────────────────────────────────────────────────────────────
// Liste « vedette » réutilisable pour la page Blocs d'accueil.
// Permet de marquer/démarquer un produit ou un service en vedette
// (même logique que le bouton étoile des pages Produits / Services).
// ─────────────────────────────────────────────────────────────

export interface FeaturedItem {
  id: number;
  label: string;
  image: string | null;
  featured: boolean;
}

interface Props {
  title: string;
  icon: string;
  hint: string;
  emptyLabel: string;
  items: FeaturedItem[];
  onToggle: (id: number, next: boolean) => void;
}

export default function FeaturedList({ title, icon, hint, emptyLabel, items, onToggle }: Props) {
  const featuredCount = items.filter((i) => i.featured).length;

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 md:p-5">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-1.5">
          <i className={`${icon} text-accent-500`}></i>
          {title}
        </h3>
        <span className="px-2 py-0.5 bg-accent-50 text-accent-700 rounded-full text-xs font-medium">
          {featuredCount} en vedette
        </span>
      </div>
      <p className="text-xs text-foreground-500 mb-4">{hint}</p>

      {items.length === 0 ? (
        <div className="text-sm text-foreground-400 flex items-center gap-2 py-3">
          <i className="ri-information-line"></i>
          {emptyLabel}
        </div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
          {items.map((item) => (
            <div
              key={item.id}
              className={`flex items-center gap-3 p-2.5 rounded-lg border transition-colors ${
                item.featured
                  ? 'bg-accent-50/70 border-accent-200'
                  : 'bg-background-50 border-background-200/70'
              }`}
            >
              <div className="w-10 h-10 rounded-md bg-background-100 overflow-hidden flex-shrink-0">
                {item.image ? (
                  <img
                    src={item.image}
                    alt={item.label}
                    className="w-full h-full object-cover"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-foreground-300">
                    <i className="ri-image-line"></i>
                  </div>
                )}
              </div>
              <span className="text-sm font-medium text-foreground-800 flex-1 min-w-0 truncate">{item.label}</span>
              <button
                type="button"
                onClick={() => onToggle(item.id, !item.featured)}
                title={item.featured ? 'Retirer de la vedette' : 'Mettre en vedette'}
                className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors cursor-pointer flex-shrink-0 ${
                  item.featured
                    ? 'text-accent-500 bg-accent-100 hover:bg-accent-200'
                    : 'text-foreground-400 hover:text-accent-500 hover:bg-accent-50'
                }`}
              >
                <i className={item.featured ? 'ri-star-fill' : 'ri-star-line'}></i>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}